import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { createElement as _createElement } from "react";
import { useEffect, useRef, useState } from 'react';
import { Box, Typography, CircularProgress, Alert, Modal, Button, TextField, Stack, IconButton, Autocomplete, Chip } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import axios from 'axios';
import { API_URL, LEAVE_API } from './shared';
import { ACCENT, ACCENT_DARK } from './leaveTheme';
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const toList = (v) => v.split(/[,;]/).map(s => s.trim()).filter(Boolean);
// Everyone with an email, from the same employee master the Employees page uses.
let peopleCache = null;
function usePeople(open) {
    const [people, setPeople] = useState(peopleCache || []);
    useEffect(() => {
        if (!open || peopleCache)
            return;
        axios.get(`${API_URL}/onboarding/employee-master`).then(r => {
            const list = r.data?.data?.employees || [];
            peopleCache = list.filter(e => e.is_current && e.email).map(e => ({ label: e.full_name || e.email, email: e.email }));
            setPeople(peopleCache);
        }).catch(() => { });
    }, [open]);
    return people;
}
/** To / Cc / Bcc: pick people by name, or type any address. Value stays a comma-separated string. */
function RecipientField({ label, value, onChange, people, disabled }) {
    const chips = toList(value);
    const nameOf = (email) => people.find(p => p.email.toLowerCase() === email.toLowerCase())?.label;
    return (_jsx(Autocomplete, { multiple: true, freeSolo: true, size: "small", disabled: disabled, options: people.map(p => p.email), value: chips, filterSelectedOptions: true, filterOptions: (opts, st) => {
            const q = st.inputValue.trim().toLowerCase();
            if (!q)
                return opts.slice(0, 50);
            return opts.filter(e => e.toLowerCase().includes(q) || (nameOf(e) || '').toLowerCase().includes(q)).slice(0, 50);
        }, getOptionLabel: o => String(o), renderOption: (props, o) => (_createElement("li", { ...props, key: String(o) },
            _jsxs(Box, { sx: { display: 'flex', flexDirection: 'column' }, children: [_jsx(Typography, { sx: { fontSize: 13, fontWeight: 600 }, children: nameOf(String(o)) || String(o) }), _jsx(Typography, { sx: { fontSize: 12, color: 'text.secondary' }, children: String(o) })] }))), onChange: (_, v) => onChange(v.flatMap(toList).join(', ')), renderTags: (vals, getTagProps) => vals.map((v, i) => {
            const { key, ...tagProps } = getTagProps({ index: i });
            const bad = !EMAIL_RE.test(String(v));
            return _jsx(Chip, { size: "small", label: nameOf(String(v)) || String(v), title: String(v), color: bad ? 'error' : 'default', variant: bad ? 'outlined' : 'filled', ...tagProps }, key);
        }), renderInput: params => _jsx(TextField, { ...params, label: label, placeholder: chips.length ? '' : 'Name or email' }) }));
}
/** Shared composer: loads a draft, lets the sender edit recipients/subject/body, then sends. */
function MailComposer({ open, loadKey, load, send, onClose }) {
    const [loading, setLoading] = useState(false);
    const [to, setTo] = useState('');
    const [cc, setCc] = useState('');
    const [bcc, setBcc] = useState('');
    const [subject, setSubject] = useState('');
    const [html, setHtml] = useState('');
    const [warning, setWarning] = useState(null);
    const [error, setError] = useState(null);
    const [sending, setSending] = useState(false);
    const bodyRef = useRef(null);
    const people = usePeople(open);
    useEffect(() => {
        if (!open || !loadKey)
            return;
        setLoading(true);
        setError(null);
        setWarning(null);
        load().then(d => { setTo(d.to || ''); setCc(d.cc || ''); setBcc(d.bcc || ''); setSubject(d.subject || ''); setHtml(d.html || ''); setWarning(d.warning || null); })
            .catch(e => setError(e?.response?.data?.message || 'Failed to load mail draft'))
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, loadKey]);
    const handleSend = async () => {
        if (sending)
            return;
        setError(null);
        if (!to.trim()) {
            setError('At least one To address is required');
            return;
        }
        const bad = [...toList(to), ...toList(cc), ...toList(bcc)].find(a => !EMAIL_RE.test(a));
        if (bad) {
            setError(`Invalid email address: ${bad}`);
            return;
        }
        setSending(true);
        try {
            await send({ to, cc, bcc, subject, html: bodyRef.current?.innerHTML ?? html });
            onClose();
        }
        catch (e) {
            setError(e?.response?.data?.message || 'Failed to send mail');
        }
        finally {
            setSending(false);
        }
    };
    return (_jsx(Modal, { open: open, onClose: () => { if (!sending)
            onClose(); }, children: _jsxs(Box, { sx: { position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
                width: { xs: '94vw', sm: 700 }, maxHeight: '90vh', overflowY: 'auto',
                bgcolor: 'white', borderRadius: 2, border: '1px solid #e2e8f0', outline: 'none' }, children: [_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2,
                        borderBottom: '1px solid #e2e8f0', position: 'sticky', top: 0, bgcolor: 'white', zIndex: 1 }, children: [_jsx(Typography, { fontSize: 16, fontWeight: 700, children: "Compose Mail" }), _jsx(IconButton, { size: "small", onClick: onClose, disabled: sending, children: _jsx(CloseIcon, { fontSize: "small" }) })] }), loading ? (_jsx(Box, { display: "flex", justifyContent: "center", py: 6, children: _jsx(CircularProgress, { size: 28 }) })) : (_jsx(Box, { sx: { p: 2.5 }, children: _jsxs(Stack, { spacing: 2, children: [warning && _jsx(Alert, { severity: "warning", sx: { fontSize: 12 }, children: warning }), _jsx(RecipientField, { label: "To", value: to, onChange: setTo, people: people, disabled: sending }), _jsx(RecipientField, { label: "Cc", value: cc, onChange: setCc, people: people, disabled: sending }), _jsx(RecipientField, { label: "Bcc", value: bcc, onChange: setBcc, people: people, disabled: sending }), _jsx(TextField, { label: "Subject", size: "small", value: subject, onChange: e => setSubject(e.target.value), disabled: sending, fullWidth: true }), _jsxs(Box, { children: [_jsx(Typography, { fontSize: 11, color: "text.secondary", mb: 0.5, children: "Body" }), _jsx(Box, { ref: bodyRef, contentEditable: !sending, suppressContentEditableWarning: true, dangerouslySetInnerHTML: { __html: html }, sx: { border: '1px solid #e2e8f0', borderRadius: 1.5, p: 1.5, minHeight: 180, fontSize: 13, overflowX: 'auto',
                                            bgcolor: sending ? '#f8fafc' : 'white', '&:focus': { outline: `1px solid ${ACCENT}` } } })] }), error && _jsx(Alert, { severity: "error", sx: { fontSize: 12 }, children: error }), _jsxs(Stack, { direction: "row", spacing: 1, justifyContent: "flex-end", children: [_jsx(Button, { onClick: onClose, disabled: sending, sx: { textTransform: 'none', fontWeight: 600 }, children: "Cancel" }), _jsx(Button, { variant: "contained", onClick: handleSend, disabled: sending, sx: { bgcolor: ACCENT, '&:hover': { bgcolor: ACCENT_DARK }, textTransform: 'none', fontWeight: 600 }, children: sending ? _jsx(CircularProgress, { size: 18, sx: { color: 'white' } }) : 'Send' })] })] }) }))] }) }));
}
/** Mail for one request (the detail dialog's "Send mail to …" button). */
export function MailComposerDialog({ open, requestId, mailType, onClose, onSent, showToast }) {
    return (_jsx(MailComposer, { open: open && !!requestId && !!mailType, loadKey: `${requestId}:${mailType}`, onClose: onClose, load: async () => (await axios.get(`${LEAVE_API}/requests/${requestId}/mail/${mailType}/draft`)).data?.data || {}, send: async (d) => {
            const res = await axios.post(`${LEAVE_API}/requests/${requestId}/mail/${mailType}/send`, d);
            showToast('Mail sent', 'success');
            onSent(res.data?.data);
        } }));
}
/** One mail covering several pending requests. */
export function CompiledMailDialog({ open, ids, onClose, onSent, showToast }) {
    return (_jsx(MailComposer, { open: open && ids.length > 0, loadKey: ids.join(','), onClose: onClose, load: async () => (await axios.post(`${LEAVE_API}/mail-compile/draft`, { ids })).data?.data || {}, send: async (d) => {
            const res = await axios.post(`${LEAVE_API}/mail-compile/send`, { ids, ...d });
            showToast(`Mail sent for ${res.data?.data?.sent ?? ids.length} request(s)`, 'success');
            onSent();
        } }));
}
